import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bell, BellRing, ShoppingBag, Volume2, VolumeX, X, User, Package, Play, CreditCard, Camera } from "lucide-react";
import { api } from "@/lib/api";

const Ctx = createContext(null);
export const useOrderAlarm = () => useContext(Ctx);

const KEY = "ismo_order_alarm";
export const SOUNDS = [
  { id: "cloche", label: "Cloche classique", type: "synth", desc: "Sonnette harmonieuse à triple résonance" },
  { id: "sirene", label: "Sirène d'urgence", type: "synth", desc: "Alerte modulée montante / descendante continue" },
  { id: "carillon", label: "Carillon mélodique", type: "synth", desc: "Mélodie 4 notes douce et élégante" },
  { id: "bip", label: "Double bip caisse", type: "synth", desc: "Impulsions courtes et dynamiques" },
  { id: "radar", label: "Radar Sci-Fi", type: "synth", desc: "Bips technologiques immersifs et rapides" },
  { id: "audio_chime", label: "Chime digital (Fichier audio)", type: "file", file: "/sounds/digital_chime.wav", desc: "Sonnerie audio WAV pure et limpide" },
  { id: "audio_pos", label: "Caisse POS moderne (Fichier audio)", type: "file", file: "/sounds/cashier_pos.wav", desc: "Son de validation de terminal de paiement" },
  { id: "audio_radar", label: "Pulse Alerte (Fichier audio)", type: "file", file: "/sounds/radar_pulse.wav", desc: "Notification audio dynamique et percutante" },
];
const loadSettings = () => {
  try {
    return { enabled: true, volume: 0.7, sound: "cloche", ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch {
    return { enabled: true, volume: 0.7, sound: "cloche" };
  }
};

const vibrate = (pattern) => {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(pattern);
  } catch {
    /* noop */
  }
};

const money = (o) => `${Number(o.total || 0).toLocaleString("fr-FR")} ${o.currency || "XOF"}`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
};

async function enableWebPush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const reg = await navigator.serviceWorker.register("/sw.js");
    const { data } = await api.get("/push/vapid-public-key");
    if (!data.public_key) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    await api.post("/push/subscribe", { subscription: sub.toJSON ? sub.toJSON() : sub });
  } catch (e) {
    /* noop */
  }
}

export function OrderAlarmProvider({ role, ordersPath, children }) {
  const [settings, setSettings] = useState(loadSettings);
  const [queue, setQueue] = useState([]);
  const sinceRef = useRef("");
  const audioCtxRef = useRef(null);
  const stopRef = useRef(null);
  const settingsRef = useRef(settings);
  const navigate = useNavigate();

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  const persist = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const ensureCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) audioCtxRef.current = new AC();
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  }, []);

  const stopAlarm = useCallback(() => {
    if (stopRef.current) {
      stopRef.current();
      stopRef.current = null;
    }
  }, []);

  const playAlarm = useCallback((volume, sound) => {
    const ctx = ensureCtx();
    if (!ctx) return;
    stopAlarm();
    const master = ctx.createGain();
    master.gain.value = Math.max(0, Math.min(1, volume));
    master.connect(ctx.destination);
    vibrate([300, 150, 300, 150, 300]);
    let stopped = false;
    const timers = [];
    const oscs = [];
    const kind = sound || "cloche";

    const track = (osc) => oscs.push(osc);

    const bell = () => {
      if (stopped) return;
      const t = ctx.currentTime;
      [660, 1320, 1980].forEach((f, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f, t);
        const peak = i === 0 ? 1 : 0.35 / i;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(peak, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
        osc.connect(g);
        g.connect(master);
        osc.start(t);
        osc.stop(t + 1.5);
        track(osc);
      });
    };

    const chime = () => {
      if (stopped) return;
      const t0 = ctx.currentTime;
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
        const t = t0 + i * 0.16;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(f, t);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.9, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
        osc.connect(g);
        g.connect(master);
        osc.start(t);
        osc.stop(t + 1);
        track(osc);
      });
    };

    const beep = () => {
      if (stopped) return;
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, t);
      osc.frequency.setValueAtTime(1175, t + 0.18);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(1, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.36);
      osc.connect(g);
      g.connect(master);
      osc.start(t);
      osc.stop(t + 0.4);
      track(osc);
    };

    const radar = () => {
      if (stopped) return;
      const t = ctx.currentTime;
      [880, 880, 1760].forEach((freq, idx) => {
        const toneTime = t + idx * 0.16;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, toneTime);
        g.gain.setValueAtTime(0.0001, toneTime);
        g.gain.exponentialRampToValueAtTime(0.8, toneTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, toneTime + 0.15);
        osc.connect(g);
        g.connect(master);
        osc.start(toneTime);
        osc.stop(toneTime + 0.16);
        track(osc);
      });
    };

    const soundObj = SOUNDS.find((s) => s.id === kind);
    let audioElem = null;

    if (soundObj?.type === "file" && soundObj.file) {
      try {
        audioElem = new Audio(soundObj.file);
        audioElem.volume = Math.max(0, Math.min(1, volume));
        audioElem.play().catch(() => {});
        const timer = setInterval(() => {
          if (stopped) return;
          try {
            audioElem.currentTime = 0;
            audioElem.play().catch(() => {});
            vibrate(200);
          } catch { /* noop */ }
        }, 2200);
        timers.push(timer);
      } catch {
        bell();
      }
    } else if (kind === "sirene") {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sawtooth";
      g.gain.value = 0.35;
      osc.connect(g);
      g.connect(master);
      const t = ctx.currentTime;
      osc.frequency.setValueAtTime(600, t);
      let tt = t;
      for (let i = 0; i < 40; i += 1) {
        osc.frequency.linearRampToValueAtTime(i % 2 === 0 ? 1200 : 600, tt + 0.7);
        tt += 0.7;
      }
      osc.start(t);
      track(osc);
    } else {
      const fn = kind === "carillon" ? chime : kind === "bip" ? beep : kind === "radar" ? radar : bell;
      fn();
      const every = kind === "carillon" ? 2000 : kind === "bip" ? 720 : kind === "radar" ? 1200 : 1600;
      timers.push(setInterval(() => {
        fn();
        vibrate(200);
      }, every));
    }

    const auto = setTimeout(() => stopAlarm(), 22000);
    stopRef.current = () => {
      stopped = true;
      timers.forEach(clearInterval);
      clearTimeout(auto);
      vibrate(0);
      if (audioElem) {
        try {
          audioElem.pause();
          audioElem.currentTime = 0;
        } catch { /* noop */ }
      }
      oscs.forEach((o) => {
        try { o.stop(); } catch { /* noop */ }
      });
      try { master.disconnect(); } catch { /* noop */ }
    };
  }, [ensureCtx, stopAlarm]);

  const testSound = useCallback(
    () => playAlarm(settingsRef.current.volume, settingsRef.current.sound),
    [playAlarm],
  );

  const requestPermission = useCallback(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") enableWebPush();
      }).catch(() => {});
    } else if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      enableWebPush();
    }
  }, []);

  const notify = useCallback((o) => {
    try {
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const isPay = o.type === "payment";
      const title = isPay
        ? (o.has_proof ? "📸 Preuve de paiement reçue — ISMO-TECH" : "💳 Paiement déclaré — ISMO-TECH")
        : "🛒 Nouvelle commande ISMO-TECH";
      const n = new Notification(title, {
        body: `N° ${o.order_number} · ${o.customer_name || "Client"} · ${money(o)}`,
        icon: o.product_image || "/logo-footer.png",
        tag: `${o.type}-${o.id}`,
        requireInteraction: true,
      });
      n.onclick = () => {
        window.focus();
        navigate(ordersPath);
        n.close();
      };
    } catch { /* noop */ }
  }, [navigate, ordersPath]);

  // Débloque l'audio au 1er geste utilisateur (politique navigateur)
  useEffect(() => {
    const resume = () => ensureCtx();
    window.addEventListener("click", resume);
    window.addEventListener("keydown", resume);
    return () => {
      window.removeEventListener("click", resume);
      window.removeEventListener("keydown", resume);
    };
  }, [ensureCtx]);

  // Active le Web Push si l'alarme est activée et la permission accordée
  useEffect(() => {
    if (settings.enabled) enableWebPush();
  }, [settings.enabled]);

  // Polling des nouvelles commandes + paiements déclarés
  useEffect(() => {
    if (!role) return undefined;
    let active = true;
    const url = role === "admin" ? "/admin/new-orders-feed" : "/vendor/new-orders-feed";
    const poll = async () => {
      try {
        const params = sinceRef.current ? { since: sinceRef.current } : {};
        const { data } = await api.get(url, { params });
        if (!active) return;
        sinceRef.current = data.server_time || sinceRef.current;
        const events = data.events || (data.orders || []).map((o) => ({ ...o, type: "order" }));
        if (events.length && settingsRef.current.enabled) {
          setQueue((q) => [...q, ...events]);
          playAlarm(settingsRef.current.volume, settingsRef.current.sound);
          events.forEach(notify);
        }
      } catch { /* noop */ }
    };
    poll();
    const timer = setInterval(poll, 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [role, playAlarm, notify]);

  const current = queue[0] || null;
  const dismiss = () => {
    stopAlarm();
    setQueue((q) => q.slice(1));
  };
  const goToOrders = () => {
    stopAlarm();
    setQueue([]);
    navigate(ordersPath);
  };

  return (
    <Ctx.Provider value={{ settings, persist, testSound, playAlarm, requestPermission, stopAlarm }}>
      {children}
      {current && (
        <OrderAlarmModal order={current} count={queue.length} onClose={dismiss} onGo={goToOrders} />
      )}
    </Ctx.Provider>
  );
}

function OrderAlarmModal({ order, count, onClose, onGo }) {
  const isPay = order.type === "payment";
  const hasProof = isPay && order.has_proof;
  const headerCls = isPay
    ? "bg-gradient-to-r from-orange-500 to-amber-600"
    : "bg-gradient-to-r from-emerald-500 to-emerald-600";
  const label = hasProof ? "Preuve de paiement reçue" : isPay ? "Paiement déclaré" : "Nouvelle commande";
  const labelClr = isPay ? "text-amber-100" : "text-emerald-100";
  const totalClr = isPay ? "text-orange-600" : "text-emerald-600";
  const goLabel = isPay ? "Vérifier le paiement" : "Voir la commande";
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="order-alarm-modal">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 order-alarm-pop">
        <div className={`relative flex items-center gap-3 px-6 py-4 text-white ${headerCls}`}>
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20 order-alarm-ring">
            {hasProof ? <Camera className="h-6 w-6" /> : isPay ? <CreditCard className="h-6 w-6" /> : <BellRing className="h-6 w-6" />}
          </span>
          <div className="leading-tight">
            <p className={`text-[11px] font-bold uppercase tracking-widest ${labelClr}`} data-testid="order-alarm-type">{label}</p>
            <p className="font-display text-lg font-extrabold">N° {order.order_number}</p>
          </div>
          {count > 1 && (
            <span className={`ml-auto rounded-full bg-white px-2.5 py-1 text-xs font-black ${totalClr}`} data-testid="order-alarm-count">
              +{count - 1} en attente
            </span>
          )}
        </div>

        <div className="flex gap-4 px-6 py-5">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-line bg-surface">
            {order.product_image ? (
              <img src={order.product_image} alt={order.product_name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-300"><Package className="h-8 w-8" /></div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 font-bold text-ink" data-testid="order-alarm-product">{order.product_name || "Produit"}</p>
            {order.items_count > 1 && <p className="text-xs text-slate-400">+ {order.items_count - 1} autre(s) article(s)</p>}
            <p className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-500">
              <User className="h-3.5 w-3.5" /> {order.customer_name || "Client"}
            </p>
            {isPay && order.payment_method && (
              <p className="mt-0.5 flex items-center gap-1.5 text-xs font-semibold text-orange-600">
                <CreditCard className="h-3.5 w-3.5" /> {order.payment_method}
              </p>
            )}
            {hasProof && (
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-bold text-orange-700" data-testid="order-alarm-proof">
                <Camera className="h-3.5 w-3.5" /> Capture d'écran jointe
              </p>
            )}
            <p className={`mt-2 font-display text-2xl font-extrabold ${totalClr}`} data-testid="order-alarm-total">{money(order)}</p>
          </div>
        </div>

        <div className="flex gap-2 border-t border-line px-6 py-4">
          <button
            data-testid="order-alarm-go"
            onClick={onGo}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-brand"
          >
            <ShoppingBag className="h-4 w-4" /> {goLabel} <ArrowRight className="h-4 w-4" />
          </button>
          <button
            data-testid="order-alarm-close"
            onClick={onClose}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-line px-4 py-3 text-sm font-bold text-slate-600 transition-colors hover:border-rose-300 hover:text-rose-600"
          >
            <X className="h-4 w-4" /> Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrderAlarmSettings() {
  const alarm = useOrderAlarm();
  if (!alarm) return null;
  const { settings, persist, testSound, requestPermission } = alarm;
  const toggle = () => {
    if (!settings.enabled) requestPermission();
    persist({ enabled: !settings.enabled });
  };
  return (
    <div className="flex items-center gap-2 rounded-full border border-line bg-white px-2.5 py-1.5" data-testid="order-alarm-settings">
      <button
        data-testid="order-alarm-toggle"
        onClick={toggle}
        title={settings.enabled ? "Alarme commandes activée" : "Alarme commandes désactivée"}
        className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
          settings.enabled ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"
        }`}
      >
        {settings.enabled ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      </button>
      <div className="hidden items-center gap-1.5 sm:flex">
        {settings.volume > 0 ? <Volume2 className="h-4 w-4 text-slate-400" /> : <VolumeX className="h-4 w-4 text-slate-400" />}
        <input
          type="range"
          min="0"
          max="100"
          value={Math.round(settings.volume * 100)}
          onChange={(e) => persist({ volume: Number(e.target.value) / 100 })}
          data-testid="order-alarm-volume"
          className="h-1.5 w-20 cursor-pointer accent-emerald-600"
          title="Volume de l'alarme"
        />
      </div>
      <select
        value={settings.sound || "cloche"}
        onChange={(e) => persist({ sound: e.target.value })}
        data-testid="order-alarm-sound"
        title="Choisir la sonnerie d'alarme"
        className="rounded-full border border-line bg-surface px-2 py-1 text-xs font-semibold text-ink outline-none focus:border-emerald-400"
      >
        {SOUNDS.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </select>
      <button
        data-testid="order-alarm-test"
        onClick={testSound}
        title="Tester le son de l'alarme"
        className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-ink"
      >
        <Play className="h-4 w-4" />
      </button>
    </div>
  );
}
